import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { carTestLabels } from "@/i18n";

const NOISE_TYPES = [
  "Motor",
  "Växellåda",
  "Fjäddring/Stöddämpare",
  "Bromsar",
  "Hjul/Lager",
  "Annat",
] as const;

interface NoisesSectionProps {
  noises: { problem?: string; type?: string; other?: string } | null;
  onNoisesChange: (noises: {
    problem?: string;
    type?: string;
    other?: string;
  }) => void;
  onNext: () => void;
}

export const NoisesSection = ({
  noises,
  onNoisesChange,
  onNext,
}: NoisesSectionProps) => {
  const [otherText, setOtherText] = useState(noises?.other || "");
  const { language } = useLanguage();
  const ct = carTestLabels(language);
  const form = ct.form ?? {};
  const v = ct.pdf?.values ?? ({} as Record<string, string>);
  const opt =
    (v as { optionValues?: Record<string, string> }).optionValues ?? {};

  const handleResultChange = (problem: string) => {
    if (problem === "no") {
      onNoisesChange({ problem });
    } else {
      onNoisesChange({ problem, type: undefined, other: undefined });
    }
  };

  const handleTypeChange = (type: string) => {
    if (type === "Annat") {
      onNoisesChange({ ...noises, type, other: otherText });
    } else {
      onNoisesChange({ ...noises, type, other: undefined });
    }
  };

  const handleOtherTextChange = (text: string) => {
    setOtherText(text);
    if (noises?.type === "Annat") {
      onNoisesChange({ ...noises, other: text });
    }
  };

  const showNextButton =
    noises?.problem === "yes" && noises?.type === "Annat" && otherText.trim();

  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-medium text-sm mb-3">{form.questionNoises}</h4>
        <div className="grid grid-cols-2 gap-1.5 mb-4">
          <Button
            onClick={() => handleResultChange("yes")}
            variant="formOption"
            className={`${
              noises?.problem === "yes"
                ? "border-accent border-2"
                : "border-gray-400"
            }`}
          >
            {v.yes}
          </Button>
          <Button
            onClick={() => handleResultChange("no")}
            variant="formOption"
            className={`${
              noises?.problem === "no"
                ? "border-accent border-2"
                : "border-gray-400"
            }`}
          >
            {v.no}
          </Button>
        </div>

        {noises?.problem === "yes" && (
          <div className="space-y-3">
            <h5 className="font-medium text-sm">{form.questionNoiseSource}</h5>
            <div className="grid grid-cols-2 gap-1.5">
              {NOISE_TYPES.map((type) => (
                <Button
                  key={type}
                  onClick={() => handleTypeChange(type)}
                  variant="formOption"
                  className={`${
                    noises?.type === type
                      ? "border-accent border-2"
                      : "border-gray-400"
                  }`}
                >
                  {opt[type] ?? type}
                </Button>
              ))}
            </div>

            {noises?.type === "Annat" && (
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder={form.placeholderDescribeNoise}
                  value={otherText}
                  onChange={(e) => handleOtherTextChange(e.target.value)}
                  className="w-full"
                />
              </div>
            )}
          </div>
        )}

        {showNextButton && (
          <Button className="w-full mt-4" onClick={onNext}>
            {form.next}
          </Button>
        )}
      </div>
    </div>
  );
};
